import { Box, Flex, Input, Text } from '@pancakeswap/uikit'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import useSWR from 'swr'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { CopyButton, ScanLink } from '../components/AddressActions'
import { fetcher, short } from '../config'
import { Chip, MONO, Pill } from '../ui'
import { MAJOR_LIST_TEXT, PoolView, canonicalQuery, classifyQuery, fmtUsd, lc, liquidityBadge, tradeLabel } from './logic'
import type { SearchResult } from './server'

type Row = PoolView
export type PickedPool = {
  pool: string
  token: string
  tokenSymbol: string
  dex: string
  kind: 'V2' | 'V3'
  pair: string
  feeLabel: string | null
  /** the pool's two tokens, for the selected-pool card */
  tokens?: Array<{ address: string; symbol: string }>
  liquidityUsd?: number | null
  /** the DEX's own word: liquidity in range now (true), none in range (false), unknown (null) */
  inRange?: boolean | null
}

const LOGOS: Record<string, string> = {
  'CADINUdex V3': '/images/dex/cadinu.png', // the real CADINU logo (public/logo.png, the site logo), 128px for crisp 32px rows on 3x screens
  'CADINUdex V2': '/images/dex/cadinu.png',
  'PancakeSwap V3': '/images/dex/pancakeswap.png',
  'PancakeSwap V2': '/images/dex/pancakeswap.png',
  'Uniswap V3': '/images/dex/uniswap.png',
  'Uniswap V2': '/images/dex/uniswap.png',
  Biswap: '/images/dex/biswap.png',
  BakerySwap: '/images/dex/bakeryswap.png',
}
export const DexLogo = ({ dex, size = 32 }: { dex: string; size?: number }) => {
  const cadinu = dex.startsWith('CADINU')
  const base = { width: size, height: size, flex: 'none', borderRadius: '50%', boxSizing: 'border-box', background: 'rgba(180,200,255,0.12)' } as const
  // a name without a version ("CADINUdex", "PancakeSwap" in the token list) finds the exchange's logo by its prefix
  const src = LOGOS[dex] ?? LOGOS[Object.keys(LOGOS).find((k) => k.toLowerCase().startsWith(dex.toLowerCase())) ?? '']
  if (!src) return <span style={base} aria-hidden="true" />
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" width={size} height={size} loading="lazy" style={{ ...base, objectFit: cadinu ? 'contain' : 'cover', padding: cadinu ? 4 : 0 }} />
}

const Badge = styled.span<{ $state: 'inactive' | 'unknown' }>`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  white-space: nowrap;
  color: rgba(190, 205, 240, ${({ $state }) => ($state === 'inactive' ? 0.9 : 0.62)});
  border: 1px ${({ $state }) => ($state === 'inactive' ? 'solid' : 'dashed')} rgba(180, 200, 255, ${({ $state }) => ($state === 'inactive' ? 0.42 : 0.3)});
  background: ${({ $state }) => ($state === 'inactive' ? 'rgba(180, 200, 255, 0.1)' : 'transparent')};
`
/** The badge says what the DEX says: Active (green) = liquidity in range now, Inactive (grey) = none in range, Unknown (muted) = could not be read. Trading is the separate line. */
function StatusBadge({ r }: { r: Row }) {
  const state = liquidityBadge(r)
  const title = state === 'active' ? 'Liquidity in range now' : state === 'inactive' ? 'No liquidity in range' : 'Liquidity state could not be read'
  if (state === 'active') return <Pill $tone="success" data-testid="v3-ps-status" title={title}>Active</Pill>
  return <Badge $state={state} data-testid="v3-ps-status" title={title}>{state === 'inactive' ? 'Inactive' : 'Unknown'}</Badge>
}

/** Trading in the last 24 h, with its data source as the tooltip. */
function TradeNote({ r }: { r: Row }) {
  const l = tradeLabel(r)
  return (
    <Text fontSize="13px" color={l.tone === 'success' ? 'success' : 'textSubtle'} data-testid="v3-ps-note" title={r.activitySource ? `Source: ${r.activitySource}` : undefined}>
      {l.text}
    </Text>
  )
}

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`
const Skel = styled.div<{ $h?: number; $w?: string }>`
  height: ${({ $h }) => $h ?? 12}px;
  width: ${({ $w }) => $w ?? '100%'};
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(180, 200, 255, 0.06) 25%, rgba(180, 200, 255, 0.16) 50%, rgba(180, 200, 255, 0.06) 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const SearchInput = styled(Input)`
  height: 48px;
  border-radius: 14px;
`
const List = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
`
const RowBtn = styled.button`
  width: 100%;
  min-width: 0;
  min-height: 72px;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  text-align: left;
  padding: 12px;
  border-radius: 16px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-width: 0;
  overflow-wrap: anywhere;
`
const Soft = styled.div`
  padding: 12px;
  border-radius: 14px;
  border: 1px dashed ${({ theme }) => theme.colors.cardBorder};
`
const Mono = styled.span`
  font-family: ${MONO};
  font-size: 12px;
`

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return [v, setV] as const
}

function Skeleton() {
  return (
    <List aria-busy="true" aria-label="Searching pools" data-testid="v3-ps-skeleton">
      {[0, 1, 2].map((i) => (
        <li key={i}>
          <Box style={{ display: 'grid', gridTemplateColumns: '32px minmax(0,1fr)', gap: 12, padding: 12, border: '1px solid rgba(180,200,255,0.18)', borderRadius: 16 }}>
            <Skel $h={32} $w="32px" style={{ borderRadius: '50%' }} />
            <Box style={{ display: 'grid', gap: 8 }}>
              <Skel $h={14} $w="55%" />
              <Skel $h={12} $w="80%" />
              <Skel $h={12} $w="65%" />
            </Box>
          </Box>
        </li>
      ))}
    </List>
  )
}

const CardBox = styled.div`
  padding: 14px;
  border-radius: 16px;
  border: 1px solid rgba(57, 217, 138, 0.45);
  background: linear-gradient(180deg, rgba(57, 217, 138, 0.1), rgba(57, 217, 138, 0.03));
  display: grid;
  gap: 12px;
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .facts {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }
  .fact {
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(180, 200, 255, 0.12);
    min-width: 0;
    small {
      display: block;
      font-size: 11px;
      color: rgba(190, 205, 240, 0.65);
    }
    b {
      font-size: 14px;
      overflow-wrap: anywhere;
    }
  }
  .lock {
    justify-self: start;
    font-size: 13px;
    color: #d7f3fb;
    text-decoration: underline;
    text-underline-offset: 3px;
    opacity: 0.85;
    &:hover { opacity: 1; }
  }
  .ver {
    padding: 0 8px;
    min-height: 20px;
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 800;
    color: #d7f3fb;
    background: rgba(127, 212, 232, 0.16);
    border: 1px solid rgba(127, 212, 232, 0.4);
  }
  .pair {
    display: inline-flex;
    margin-right: 4px;
    > span + span {
      margin-left: -8px;
    }
  }
`

/** The pool the user chose: exchange with its version, the pair with token logos, fee tier, liquidity and whether it is in range, the pool address with copy and BscScan, and Change. */
export function PickedChip({ picked, onClear }: { picked: PickedPool; onClear: () => void }) {
  const status = picked.inRange === true ? 'Active (liquidity in range)' : picked.inRange === false ? 'Inactive (none in range)' : 'Status unknown'
  return (
    <CardBox data-testid="v3-ps-picked">
      <div className="row">
        <DexLogo dex={picked.dex} size={44} />
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Flex alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
            <Text bold fontSize="16px" color="success" data-testid="v3-ps-picked-dex">{picked.dex.replace(/ V[23]$/, '')}</Text>
            <span className="ver">{picked.kind}</span>
            <Text color="success" fontSize="13px">✓ Selected</Text>
          </Flex>
          <Flex alignItems="center" style={{ gap: 6 }} mt="2px">
            <span className="pair">
              {(picked.tokens ?? []).map((t) => <TokenLogo key={t.address} address={t.address} symbol={t.symbol} size={22} />)}
            </span>
            <Text bold fontSize="15px" data-testid="v3-ps-picked-pair">{picked.pair}</Text>
          </Flex>
        </Box>
        <Chip type="button" onClick={onClear} data-testid="v3-ps-change">Change</Chip>
      </div>
      <div className="facts">
        <div className="fact"><small>Fee tier</small><b>{picked.feeLabel ?? (picked.kind === 'V2' ? 'Fixed' : '—')}</b></div>
        <div className="fact"><small>Liquidity</small><b>{picked.liquidityUsd === null || picked.liquidityUsd === undefined ? '—' : fmtUsd(picked.liquidityUsd)}</b></div>
        <div className="fact"><small>Status</small><b data-testid="v3-ps-picked-status">{status}</b></div>
      </div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <Text fontSize="13px" color="textSubtle">
          Pool <Mono title={picked.pool}>{short(picked.pool)}</Mono> · Project token {picked.tokenSymbol} <Mono>{short(picked.token)}</Mono>
        </Text>
        <Flex alignItems="center">
          <CopyButton value={picked.pool} label="pool address" testId="v3-ps-copy" />
          <ScanLink value={picked.pool} label="pool" testId="v3-ps-scan" />
        </Flex>
      </div>
      <a className="lock" href="/cadinu-lock" data-testid="v3-ps-lock">🔒 Lock this pool&apos;s liquidity to earn the &quot;LP locked&quot; tick</a>
    </CardBox>
  )
}

/** Search by token address or symbol, list its verified pools on the supported exchanges, pick one. */
export default function PoolSearch({ picked, onPick, onClear, initialQuery, autoPickPool }: {
  picked: PickedPool | null
  onPick: (p: PickedPool) => void
  onClear: () => void
  /** a token address (or symbol) searched on arrival, e.g. from a token page */
  initialQuery?: string
  /** when this pool is among the results it is picked once, with the searched token as the project token */
  autoPickPool?: string
}) {
  const [q, setQ] = useState(initialQuery ?? '')
  const [applied, setApplied] = useDebounced(q.trim(), 400)
  const [choosing, setChoosing] = useState<string | null>(null)
  const live = useMemo(() => classifyQuery(q), [q])
  const parsed = useMemo(() => classifyQuery(applied), [applied])
  const debouncing = q.trim() !== applied && live.mode !== 'invalid'
  const url = parsed.mode === 'invalid' ? null : `/api/competition-v3/pool-search?q=${encodeURIComponent(canonicalQuery(parsed))}`
  const { data, error, isLoading, mutate } = useSWR<SearchResult>(url, fetcher, { revalidateOnFocus: false, shouldRetryOnError: false, dedupingInterval: 30_000 })
  const current = data && parsed.mode !== 'invalid' && lc(data.query) === lc(canonicalQuery(parsed)) ? data : undefined

  const pick = (r: Row, token: string) => {
    const t = [r.token0, r.token1].find((x) => lc(x.address) === lc(token))
    setChoosing(null)
    onPick({ pool: r.pool, token, tokenSymbol: t?.symbol ?? short(token), dex: r.dex, kind: r.kind, pair: r.pair, feeLabel: r.feeLabel, tokens: [r.token0, r.token1].map((x) => ({ address: x.address, symbol: x.symbol })), liquidityUsd: r.liquidityUsd, inRange: r.inRange })
  }
  const select = (r: Row) => (r.projectOptions.length === 1 ? pick(r, r.projectOptions[0]) : setChoosing(choosing === r.pool ? null : r.pool))

  // arriving from a token page: pick the wanted pool once its results are in (never again after the user changed it)
  const autoPicked = useRef(false)
  useEffect(() => {
    if (autoPicked.current || !autoPickPool || picked || !current) return
    const r = current.pools.find((p) => lc(p.pool) === lc(autoPickPool))
    const token = r?.preferredProject ?? r?.projectOptions[0]
    if (!r || !token) return
    autoPicked.current = true
    pick(r, token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, picked, autoPickPool])

  if (picked) return <PickedChip picked={picked} onClear={onClear} />

  let body: React.ReactNode
  if (debouncing || (url && isLoading && !current)) body = <Skeleton />
  else if (live.mode === 'invalid' && q.trim()) {
    const hint = live.reason === 'partial_address' ? 'Paste the full token address (0x followed by 40 characters).' : live.reason === 'too_short' ? 'Type at least two characters.' : 'Use letters and numbers only, or paste a token address.'
    body = <Text fontSize="13px" color="textSubtle" mt="8px" data-testid="v3-ps-hint">{hint}</Text>
  } else if (!q.trim()) {
    body = <Text fontSize="13px" color="textSubtle" mt="8px" data-testid="v3-ps-idle">Paste a token address or type a symbol.</Text>
  } else if (error) {
    body = (
      <Soft style={{ marginTop: 12 }} role="alert" data-testid="v3-ps-error">
        <Text bold fontSize="14px">Search is unavailable right now.</Text>
        <Text fontSize="13px" color="textSubtle" mb="8px">Nothing was changed. Try again, or enter the pool address under Advanced.</Text>
        <Chip type="button" onClick={() => mutate()} data-testid="v3-ps-retry">Try again</Chip>
      </Soft>
    )
  } else if (current?.major) {
    body = (
      <Soft style={{ marginTop: 12 }} role="alert" data-testid="v3-ps-major">
        <Text bold fontSize="14px">{current.major.symbol} is a major asset and cannot be the project token.</Text>
        <Text fontSize="13px" color="textSubtle">{MAJOR_LIST_TEXT} are only the other side of a pool. Search for the project&apos;s own token instead.</Text>
      </Soft>
    )
  } else if (current?.notice === 'position_not_found') {
    body = (
      <Soft style={{ marginTop: 12 }} data-testid="v3-ps-empty">
        <Text bold fontSize="14px">Position {current.query} was not found on CADINUdex V3.</Text>
        <Text fontSize="13px" color="textSubtle">Check the number, or search by the token address or symbol.</Text>
      </Soft>
    )
  } else if (current && current.pools.length === 0) {
    body = (
      <Soft style={{ marginTop: 12 }} data-testid="v3-ps-empty">
        <Text bold fontSize="14px">No pools found for {current.query.length > 12 ? short(current.query) : current.query}.</Text>
        <Text fontSize="13px" color="textSubtle">
          None on the supported exchanges were verified{current.hidden ? ` (${current.hidden} hidden: could not be verified)` : ''}. Check the address or symbol, or enter the pool under Advanced.
        </Text>
      </Soft>
    )
  } else if (current) {
    body = (
      <>
        <Text fontSize="12px" color="textSubtle" mt="12px" data-testid="v3-ps-count">
          {current.pools.length} verified {current.pools.length === 1 ? 'pool' : 'pools'} · Active liquidity first, then trades in 24h, then liquidity
        </Text>
        <List data-testid="v3-ps-list">
          {current.pools.map((r) => (
            <li key={r.pool}>
              <RowBtn type="button" onClick={() => select(r)} data-testid="v3-ps-row" data-pool={r.pool} data-dex={r.dex} aria-expanded={r.projectOptions.length > 1 ? choosing === r.pool : undefined}>
                <DexLogo dex={r.dex} />
                <Box style={{ minWidth: 0 }}>
                  <Flex justifyContent="space-between" alignItems="center" style={{ gap: 8 }}>
                    <Text bold fontSize="15px" style={{ overflowWrap: 'anywhere', minWidth: 0 }} data-testid="v3-ps-dex">{r.dex}</Text>
                    <StatusBadge r={r} />
                  </Flex>
                  <Line>
                    <Text bold fontSize="14px" data-testid="v3-ps-pair">{r.pair}</Text>
                    {r.feeLabel && <Pill $tone="info" data-testid="v3-ps-fee">{r.feeLabel}</Pill>}
                    {r.projectOptions.length > 1 && <Text fontSize="12px" color="textSubtle">choose the project token</Text>}
                  </Line>
                  <Line style={{ marginTop: 4 }}>
                    <Text fontSize="13px" color="textSubtle">
                      Liquidity <b style={{ color: 'inherit' }}>{r.liquiditySource === 'chain' ? '≈' : ''}{fmtUsd(r.liquidityUsd)}</b>
                    </Text>
                    <Text fontSize="13px" color="textSubtle">
                      24h volume <b>{r.volume24hUsd === null ? '—' : fmtUsd(r.volume24hUsd)}</b>
                    </Text>
                    <TradeNote r={r} />
                    {r.noLiquidity && <Text fontSize="13px" color="warning" data-testid="v3-ps-warn">No liquidity</Text>}
                  </Line>
                </Box>
              </RowBtn>
              {choosing === r.pool && (
                <Box mt="8px" p="12px" style={{ borderRadius: 14, border: '1px solid rgba(180,200,255,0.18)' }} data-testid="v3-ps-choose">
                  <Text bold fontSize="14px" mb="8px">Which token is the project token?</Text>
                  <Flex style={{ gap: 8 }} flexWrap="wrap">
                    {r.projectOptions.map((a) => {
                      const t = [r.token0, r.token1].find((x) => lc(x.address) === lc(a))
                      return (
                        <Chip key={a} type="button" $on={lc(r.preferredProject) === lc(a)} onClick={() => pick(r, a)} data-testid="v3-ps-choose-option">
                          Use {t?.symbol ?? short(a)}
                        </Chip>
                      )
                    })}
                  </Flex>
                </Box>
              )}
            </li>
          ))}
        </List>
        {current.hidden > 0 && <Text fontSize="12px" color="textSubtle" mt="8px">{current.hidden} more hidden: they could not be verified on a supported exchange.</Text>}
        {current.sources.gecko !== 'ok' && <Text fontSize="12px" color="warning" mt="8px" data-testid="v3-ps-degraded">Market data is temporarily unavailable, so volume may be missing. Pools shown are still verified on-chain.</Text>}
        <Text fontSize="12px" color="textSubtle" mt="8px">Liquidity marked ≈ is estimated from the pool&apos;s balances.</Text>
      </>
    )
  }

  return (
    <Box data-testid="v3-ps">
      <SearchInput
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setChoosing(null)
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') setApplied(q.trim())
          if (e.key === 'Escape') setQ('')
        }}
        placeholder="Token address, symbol (CADINU) or position #142"
        aria-label="Search pools by token address or symbol"
        inputMode="search"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        data-testid="v3-search"
      />
      <Box aria-live="polite">{body}</Box>
    </Box>
  )
}
